import { Accordion } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ReferenceResource,
  ReferenceResourceCategoryEnum,
  ReferenceResourceSchema,
  ReferenceResourceTypeEnum,
} from '@monetize/types/app';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  doGetReferenceResourceById,
  useReferenceResource,
  useReferenceResources,
  useSaveReferenceResource,
  useUpdateReferenceResource,
} from '../../../../../api/settingsService';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MInput,
  MStack,
  MVStack,
} from '../../../../../components/Monetize';
import { MFileDragDropUpload } from '../../../../../components/Monetize/MFileDragDropUpload';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { PreviewStorageFile } from '../../../../../components/Monetize/PreviewFile/PreviewStorageFile';
import { useAccordionControl } from '../../../../../hooks';
import { base64ToArrayBuffer } from '../../../../../utils';
import { downloadBlobAsFile } from '../../../../../utils/download';
import { SettingsSection } from '../../../components/SettingsSection';
import { ReferenceResourceTable } from './ReferenceResourceTable';
import ReplaceButton from './ReplaceButton';

const defaultContent = {
  id: '',
  name: '',
  description: '',
  resourceType: ReferenceResourceTypeEnum.FILE,
  category: ReferenceResourceCategoryEnum.DEAL_ROOM,
  resourceContent: {
    storageObjectId: '',
    storageObject: null,
  },
  isLocked: false,
};

export const DealRoomSettingMarketingRepository = () => {
  const { accordionValue, handleAccordionChange } = useAccordionControl();
  const { mutateAsync: doSaveDealRoomReferenceResource } =
    useSaveReferenceResource();
  const { mutateAsync: doUpdateDealRoomReferenceResource } =
    useUpdateReferenceResource();
  const { mutateAsync: doDeleteDealRoomReferenceResource } =
    useReferenceResource();

  const { isLoading, data: referenceResources } = useReferenceResources({
    filters: {
      category: ReferenceResourceCategoryEnum.DEAL_ROOM,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(ReferenceResourceSchema),
    mode: 'onBlur',
    defaultValues: { ...defaultContent },
  });
  const isValid = Object.keys(errors).length === 0;

  const [currentContent, setCurrentContent] =
    useState<ReferenceResource>(defaultContent);
  const [isModalOpen, toggleModalOpen] = useState(false);
  const [isSaving, toggleSaving] = useState(false);
  const [isEditing, toggleEditing] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  const onClose = () => {
    toggleModalOpen(false);
  };

  const onNew = () => {
    setCurrentContent(defaultContent);
    reset(defaultContent);
    setAttachmentFile(null);
    toggleEditing(false);
    toggleModalOpen(true);
  };

  const onDropAccepted = useCallback(async (acceptedFiles: File) => {
    setAttachmentFile(acceptedFiles);
  }, []);

  const onEdit = (data: ReferenceResource) => {
    setCurrentContent(data);
    reset(data);
    toggleEditing(true);
    toggleModalOpen(true);
  };

  const onDownload = async (data: ReferenceResource) => {
    try {
      const referenceResource = await doGetReferenceResourceById(data.id);
      if (referenceResource.resourceContent.storageObject) {
        const document = base64ToArrayBuffer(
          referenceResource.resourceContent?.storageObject
            ?.contentBase64Encoded!,
        );
        downloadBlobAsFile(
          document,
          referenceResource.resourceContent?.storageObject.filename,
        );
      }
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const onDelete = async (data: ReferenceResource) => {
    await doDeleteDealRoomReferenceResource({ id: data.id });
  };

  const onReplace = () => {
    setCurrentContent({
      ...currentContent,
      resourceContent: {
        storageObjectId: '',
      },
    });
    setAttachmentFile(null);
  };

  const doGetDocument = async () => {
    const referenceResource = await doGetReferenceResourceById(
      currentContent.id,
    );
    if (referenceResource.resourceContent.storageObject) {
      return base64ToArrayBuffer(
        referenceResource.resourceContent.storageObject.contentBase64Encoded!,
      );
    }
    return null;
  };

  const onSubmit = async (data: ReferenceResource) => {
    try {
      toggleSaving(true);

      if (!isEditing) {
        await doSaveDealRoomReferenceResource({
          ...currentContent,
          ...data,
          file: attachmentFile,
        });
      } else {
        await doUpdateDealRoomReferenceResource({
          ...currentContent,
          ...data,
          file: attachmentFile,
        });
      }
      toggleModalOpen(false);
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      toggleSaving(false);
    }
  };

  return (
    <Accordion.Root
      multiple
      w="full"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem
        value="0"
        label="Marketing Repository"
        description="Upload documents that are optionally available to be included in Deal Rooms."
      >
        <MFlex justifyContent="flex-end"></MFlex>
        <MBox w="100%" pb={4}>
          <MVStack alignItems="start" gap={4}>
            <SettingsSection label="" showDivider={false}>
              <MBox w="100%" display="flex" justifyContent="flex-start" mb="2">
                <MButton variant="primary" onClick={onNew}>
                  Add New Item
                </MButton>
              </MBox>

              <ReferenceResourceTable
                data={referenceResources}
                isLoading={isLoading}
                onEdit={onEdit}
                onDownload={onDownload}
                onDelete={onDelete}
              />
            </SettingsSection>
          </MVStack>
        </MBox>
      </MSettingAccordionItem>

      <MCenterModal
        isOpen={isModalOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        modalTitle={currentContent.id ? 'Edit ' : 'New '}
        modalHeaderProps={{ pb: 1 }}
      >
        <form>
          <MBox flex="1">
            <MFormField label="Title" error={errors?.name} isRequired>
              <Controller
                name={`name`}
                control={control}
                render={({ field: { value, ...field } }) => (
                  <MInput value={value || ''} {...field} />
                )}
              />
            </MFormField>

            <MFormField mt={4} label="Description" error={errors?.description}>
              <Controller
                name={`description`}
                control={control}
                render={({ field: { value, ...field } }) => (
                  <MInput value={value || ''} {...field} />
                )}
              />
            </MFormField>
            {(!isEditing || !currentContent.resourceContent.storageObject) && (
              <MFormField mt={4} label="File" isRequired>
                <MFileDragDropUpload
                  w="full"
                  accept={['pdf', 'docx', 'xlsx', 'ppt']}
                  label="Drag and Drop your file here or Browse"
                  onFileUpload={onDropAccepted}
                  maxFileSize={10}
                  innerContainerProps={{ p: '5', bg: 'white' }}
                  selectedFile={attachmentFile}
                />
              </MFormField>
            )}
            {isEditing && currentContent.resourceContent.storageObject && (
              <>
                <ReplaceButton onReplace={onReplace} mt={4} />
                <PreviewStorageFile
                  file={currentContent.resourceContent.storageObject}
                  getDocument={doGetDocument}
                />
              </>
            )}

            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
              mt={4}
            >
              <MButton
                variant="cancel"
                onClick={() => toggleModalOpen(false)}
                minW="auto"
              >
                Cancel
              </MButton>
              <MButton
                variant="primary"
                isLoading={isSaving}
                onClick={handleSubmit(onSubmit)}
                minW="auto"
                isDisabled={(!isEditing && !attachmentFile) || !isValid}
              >
                Save
              </MButton>
            </MStack>
          </MBox>
        </form>
      </MCenterModal>
    </Accordion.Root>
  );
};
